import {
    CdkDragDrop,
    DragDropModule,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import {
    BookmarkPromptDialogComponent,
    BookmarkPromptDialogResult,
} from 'app/layout/common/bookmark-prompt-dialog/bookmark-prompt-dialog.component';
import { FavoriteToggleComponent } from 'app/layout/common/favorite-toggle/favorite-toggle.component';
import { ShortcutsService } from 'app/layout/common/shortcuts/shortcuts.service';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'shortcuts',
    templateUrl: './shortcuts.component.html',
    styleUrls: ['./shortcuts.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'shortcuts',
    imports: [
        DragDropModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgTemplateOutlet,
        RouterLink,
        FavoriteToggleComponent,
    ],
})
export class ShortcutsComponent implements OnInit, OnDestroy {
    @ViewChild('shortcutsOrigin') private _shortcutsOrigin: MatButton;
    @ViewChild('shortcutsPanel') private _shortcutsPanel: TemplateRef<any>;

    mode: 'view' | 'modify' = 'view';
    shortcuts: Shortcut[];
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _shortcutsService: ShortcutsService,
        private _dialog: MatDialog,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the shortcuts
        this._shortcutsService.shortcuts$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((shortcuts: Shortcut[]) => {
                // Load the shortcuts
                this.shortcuts = shortcuts;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the shortcuts panel
     */
    openPanel(): void {
        // Return if the shortcuts panel or its origin is not defined
        if (!this._shortcutsPanel || !this._shortcutsOrigin) {
            return;
        }

        // Make sure to start in 'view' mode
        this.mode = 'view';

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._shortcutsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the shortcuts panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Change the mode
     */
    changeMode(mode: 'view' | 'modify'): void {
        this.mode = mode;
    }

    /**
     * Remove a shortcut
     */
    removeShortcut(event: MouseEvent, shortcut: Shortcut): void {
        event.preventDefault();
        event.stopPropagation();

        // The service reports failures via snackbar; nothing further to do here.
        this._shortcutsService.delete(shortcut.id).subscribe({
            error: () => {},
        });
    }

    /**
     * Edit a shortcut's name and icon in the same dialog that creates one
     */
    editShortcut(event: MouseEvent, shortcut: Shortcut): void {
        event.preventDefault();
        event.stopPropagation();

        this._dialog
            .open(BookmarkPromptDialogComponent, {
                width: '600px',
                maxHeight: '90vh',
                data: {
                    mode: 'edit',
                    label: shortcut.label,
                    icon: shortcut.icon,
                },
            })
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result: BookmarkPromptDialogResult) => {
                if (!result) {
                    return;
                }

                this._shortcutsService.update(shortcut.id, result).subscribe({
                    next: () => this._changeDetectorRef.markForCheck(),
                    error: () => {},
                });
            });
    }

    /**
     * Persist a drag-and-drop re-order, falling back to the server's order if it fails
     */
    drop(event: CdkDragDrop<Shortcut[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const reordered = [...this.shortcuts];
        moveItemInArray(reordered, event.previousIndex, event.currentIndex);

        this.shortcuts = reordered;
        this._changeDetectorRef.markForCheck();

        // The server rejects a partial list, so always send every id.
        this._shortcutsService
            .reorder(reordered.map((item) => item.id))
            .subscribe({
                error: () => {
                    this._shortcutsService
                        .getAll()
                        .subscribe({ error: () => {} });
                },
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._shortcutsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
